import Ellipsis from "@/components/views/Ellipsis";
import { cssVariables } from "@/utils/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration, useGetAgentEnumerations } from "../../../../common/api/queries";
import { ACCESS_TREE_PATH_DELIMITER } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { useCurrentAgentAccount, useHasPermissions } from "../../../auth/hooks";
import { EnumerationType } from "../../../enumerations/enums";
import { CommissionsSettingsLevelEnumeration, CommissionsSettingsRankEnumeration } from "../../../enumerations/types";
import { AgentBase } from "../../types";
import { formatAgentIdNumber } from "../../utils";
import { AgentTypeTagWithName } from "../tags/AgentTypeTagWithName";

type SuperiorAgent = AgentBase & {
  commissionsSettingsLevel?: CommissionsSettingsLevelEnumeration;
  commissionSettingsRank?: CommissionsSettingsRankEnumeration;
};

type Props<T extends AgentBase> = {
  agent: T;
};

export const SuperiorAgentsTable = <T extends AgentBase>({ agent }: Props<T>) => {
  const { t } = useTranslation();

  const agentUserAccount = useCurrentAgentAccount();
  const commissionsSettingsLevelEnumerations = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_LEVELS);
  const commissionsSettingsRankEnumerations = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_RANKS);

  const hasReadAgentPermission = useHasPermissions(Permission.AGENT_READ);

  const superiorAgentIds = agent.accessTreePath?.split(ACCESS_TREE_PATH_DELIMITER);

  const { data: superiorAgentsEnumeration, isLoading } = useGetAgentEnumerations(superiorAgentIds ?? []);

  const superiorAgentsData = superiorAgentsEnumeration?.map<SuperiorAgent>((agentItem) => {
    const level = commissionsSettingsLevelEnumerations.find(
      (level) => level.id === agentItem.commissionsSettingsLevelId,
    );
    const rank = commissionsSettingsRankEnumerations.find((rank) => rank.id === agentItem.commissionsSettingsRankId);

    return { ...agentItem, commissionsSettingsLevel: level, commissionSettingsRank: rank };
  });

  const superiorAgents = superiorAgentIds
    ?.map((agentId) => superiorAgentsData?.find((agent) => agent.id === agentId))
    .filter((agent) => agent !== undefined);

  const columns: ColumnsType<SuperiorAgent> = useMemo(
    () => [
      {
        key: "aggregatedName",
        title: t("agent.attrs.aggregatedName"),
        width: 180,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <AgentTypeTagWithName
            textStyle={record.deactivated ? { color: cssVariables.colorRed } : undefined}
            agent={record}
            hideIdNumber
            ellipsis
            showLink={
              hasReadAgentPermission &&
              !!agentUserAccount?.agent.id &&
              record.accessTreePath?.includes(agentUserAccount?.agent.id)
            }
          />
        ),
      },
      {
        key: "idNumber",
        title: t("agent.attrs.idNumber"),
        width: 110,
        render: (_, record) => formatAgentIdNumber(record),
      },
      {
        key: "commissionsSettingsLevel",
        title: t("agent.helpers.commissionsSettingsLevel"),
        width: 100,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.commissionsSettingsLevel?.code}</Ellipsis>,
      },
      {
        key: "commissionsSettingsRank",
        title: t("agent.helpers.commissionsSettingsRank"),
        width: 160,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.commissionSettingsRank?.name}</Ellipsis>,
      },
    ],
    [t, hasReadAgentPermission, agentUserAccount],
  );

  return (
    <Table
      className="margin-bottom-tiny"
      loading={isLoading}
      scroll={{ x: 550 }}
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={superiorAgents}
      columns={columns}
    />
  );
};
